import * as React from 'react';
import { get } from 'lodash';

import { navigate } from 'libs/router';

import EntityName from 'components/entity/entity-name/EntityName';
import SortableTable from 'components/sortable-table/SortableTable';
import PageSection from 'components/page/page-section/PageSection';
import SentimentSelect from 'components/sentiment-select/SentimentSelect';
import SentimentAnalysisMediaPreview from '../SentimentAnalysisMediaPreview';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Entity from 'components/entity/Entity';
import StatusLabel from 'components/status-label/StatusLabel';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButtonDropdown from 'components/buttons/action-button-dropdown/ActionButtonDropdown';

import { __t, applyTranslations, t } from 'utils/language';
import { getEntityFromProfile } from 'utils/view';
import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';

import './admin-sentiment-analysis-table.scss';

applyTranslations(require('./admin-sentiment-analysis-table.i18n.json'));

import type {
  ActionButtonDropdownItem,
  Data,
  LocationWindow,
  PageSectionTitle,
  SentimentAnalysisItem,
  SentimentClass,
  SortableTableColumnType,
  SortableTableSort
} from 'types';

const DEFAULT_SORTING = 'updated_date.desc';

type Props = {
  location: LocationWindow,
  items: Array<SentimentAnalysisItem>,
  itemsCount: number,
  onChange: (item: SentimentAnalysisItem, sentimentClass: SentimentClass) => void,
  onOptionClick: (identifier: string) => void
};

type SentimentAnalysisOptionBtn = {
  onOptionSelect: (items: Array<ActionButtonDropdownItem>, data?: Data | null) => void,
  options: Array<ActionButtonDropdownItem>
};

const SENTIMENT_TABLE_OPTIONS: Array<ActionButtonDropdownItem> = [
  {
    identifier: __t('admin_sentiment_analysis_table.excel_select_value'),
    value: __t('admin_sentiment_analysis_table.excel_select_value'),
    label: __t('admin_sentiment_analysis_table.excel_label_value'),
    isDisabled: false,
    isFocused: false,
    isSelected: false
  }
];

const CustomButtonComponent = (): JSX.Element => <SVGDynamicIcon
  name="three-dots-vertical-custom-fg"
  customColor="grey"
  width={24}
  height={24}
/>;

const SentimentAnalysisTableOptionsBtn = (
  {
    onOptionSelect,
    options
  }: SentimentAnalysisOptionBtn
): JSX.Element => (
  <ActionButtonDropdown
    name="abd-with-custom-button"
    items={options}
    onClick={onOptionSelect}
    customButton={() => <CustomButtonComponent />}
  />
);


const getColumns = (): Array<SortableTableColumnType> => ([
  {
    title: __t('admin_sentiment_analysis_table.columns.influencer'),
    key: 'creator_name',
    width: 200,
    render: (col) => {
      const entity = getEntityFromProfile(col.influencerProfile);
      const entityUrl = `/content-creator/profiles/${entity?.id}`;

      return (
        <div className="admin-sentiment-analysis-table__creator-name">
          <Entity>
            <EntityAvatar
              bordered
              entity={entity}
              link={entityUrl}
              openInNewTab
            />
            <EntityName
              size="inherit"
              entity={entity}
              link={entityUrl}
              openInNewTab
            />
          </Entity>
        </div>
      );
    }
  },
  {
    title: __t('admin_sentiment_analysis_table.columns.task_type'),
    width: 96,
    key: 'task_types',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sort: (commentA, commentB) => 0,
    render: (col) => (
      <div className="admin-sentiment-analysis-table__task-type">
        <SVGDynamicIcon
          name={`channel-${col.commentPostType.split('_')[0]}-custom-fg`}
          width={16}
          height={16}
        />
        &nbsp;
        {__t(`admin_sentiment_analysis_table.task_type.${col.commentPostType}`)}
      </div>
    )
  },
  {
    title: __t('admin_sentiment_analysis_table.columns.asset'),
    width: 70,
    key: 'deliverable',
    align: 'center',
    render: (col) => (
      <SentimentAnalysisMediaPreview
        url={col.post.url}
        thumbnailUrl={col.post.thumbnailUrl}
        caption={col.post.caption}
      />
    )
  },
  {
    title: __t('admin_sentiment_analysis_table.columns.comment'),
    key: 'comment',
    render: (col) => (
      <div className="text-overflow-wrap text-preserve-whitespaces admin-sentiment-analysis-table__comment">
        {
          col.isReply &&
            <div className="admin-sentiment-analysis-table__reply">
              <Tooltip
                message={__t('admin_sentiment_analysis_table.reply_tooltip')}
              >
                <StatusLabel
                  size="tiny"
                  label={__t('admin_sentiment_analysis_table.reply_label')}
                  type="neutral"
                />
              </Tooltip>
            </div>
        }
        {col.comment}
      </div>
    )
  },
  {
    title: __t('admin_sentiment_analysis_table.columns.sentiment_score'),
    titleTooltipMsg: __t('admin_sentiment_analysis_table.columns.sentiment_score_tooltip'),
    key: 'sentiment_score',
    width: 80,
    align: 'center',
    render: (col) => col.sentimentScore.toFixed(2)
  },
  {
    title: __t('admin_sentiment_analysis_table.columns.sentiment_class'),
    key: 'sentiment_class',
    width: 128,
    align: 'center',
    render: () => null // is overwritten in `render` below
  }
]);

class SentimentAnalysisTable extends React.PureComponent<Props> {
  handleSortChanged = (
    {
      sortingKey,
      ascending
    }: SortableTableSort
  ): void => {
    const { location } = this.props;

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, {
      sort: encodeURIComponent(`${sortingKey}.${ascending ? 'asc' : 'desc'}`)
    })));
  };

  handleOptionClick = (data: Array<ActionButtonDropdownItem>, item?: Data | null): void => {
    const identifier = item ? item.identifier : null;
    if (identifier) {
      this.props.onOptionClick(identifier);
    }
  };

  render() {
    const { items, itemsCount, location, onChange } = this.props;
    const columns = getColumns();
    const sort = encodeURIComponent(get(location, 'query.sort', DEFAULT_SORTING)).split('.');
    let sentimentTableOptions = SENTIMENT_TABLE_OPTIONS;

    if (!items.length) {
      sentimentTableOptions = SENTIMENT_TABLE_OPTIONS.map((option) => {
        if (option.identifier === __t('admin_sentiment_analysis_table.excel_select_value')) {
          return { ...option, isDisabled: true };
        }
        return option;
      });
    }
    const PAGE_SECTION_PROPS: PageSectionTitle = {
      color: 'normal',
      rightComponent: <SentimentAnalysisTableOptionsBtn onOptionSelect={this.handleOptionClick} options={sentimentTableOptions} />
    };
    const tableColumns = columns.map(column => (
      column.key === 'sentiment_class' ?
        {
          ...column,
          render: (col) => (
            <SentimentSelect
              value={col.sentimentClass}
              onChange={sentimentClass => onChange(col, sentimentClass)}
            />
          )
        } :
        column
    ));

    return (
      <PageSection
        padding="none"
        title={`${String(itemsCount)} ${__t('admin_sentiment_analysis_table.results_label')}`}
        titleProps={PAGE_SECTION_PROPS}
        customClass="admin-sentiment-analysis-table"
      >
        <SortableTable
          noScroll
          data={items}
          columns={tableColumns}
          defaultSortingKey={sort[0]}
          defaultSortingDirection={sort[1] === 'asc' ? 'ascending' : 'descending'}
          onSortChanged={this.handleSortChanged}
          emptyMessage={location.query && location.query.deliverableId ? t('sortable_table_no_data') : __t('admin_sentiment_analysis_table.no_deliverable_selected')}
        />
      </PageSection>
    );
  }
}

export default SentimentAnalysisTable;
